'use client';

import React, { useState } from 'react';
import AppLogo from '@/components/ui/AppLogo';

type Role = 'guest' | 'host';

export default function SignupPage() {
  const [role, setRole] = useState<Role>('guest');
  const [step, setStep] = useState<1 | 2>(1);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    agreeTerms: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const update = (field: string, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors({});
  };

  const validateStep1 = () => {
    const errs: Record<string, string> = {};
    if (!form.firstName.trim()) errs.firstName = 'First name is required';
    if (!form.lastName.trim()) errs.lastName = 'Last name is required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) errs.email = 'Enter a valid email address';
    if (form.phone && !/^\+?[\d\s\-()]{7,}$/.test(form.phone)) errs.phone = 'Enter a valid phone number';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateStep2 = () => {
    const errs: Record<string, string> = {};
    if (!form.password || form.password.length < 8) errs.password = 'Password must be at least 8 characters';
    if (form.password !== form.confirmPassword) errs.confirmPassword = 'Passwords do not match';
    if (!form.agreeTerms) errs.agreeTerms = 'You must agree to the terms';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => {
    if (validateStep1()) setStep(2);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep2()) return;
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      window.location.href = role === 'host' ? '/host-dashboard' : '/guest-dashboard';
    }, 1400);
  };

  const passwordStrength = (pw: string) => {
    if (!pw) return 0;
    let score = 0;
    if (pw.length >= 8) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    return score;
  };

  const strength = passwordStrength(form.password);
  const strengthLabel = ['', 'Weak', 'Fair', 'Good', 'Strong'][strength];
  const strengthColors = ['', '#EF4444', '#F5A623', '#4088C6', '#22C55E'];

  const inputStyle = (hasError: boolean) => ({
    background: hasError ? 'rgba(239,68,68,0.1)' : 'rgba(10,22,40,0.7)',
    border: hasError ? '1px solid rgba(239,68,68,0.5)' : '1px solid rgba(64,136,198,0.25)',
    boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.3)',
  });

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.border = '1px solid rgba(64,136,198,0.6)';
    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(64,136,198,0.1), inset 0 1px 3px rgba(0,0,0,0.3)';
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>, hasError: boolean) => {
    e.currentTarget.style.border = hasError ? '1px solid rgba(239,68,68,0.5)' : '1px solid rgba(64,136,198,0.25)';
    e.currentTarget.style.boxShadow = 'inset 0 1px 3px rgba(0,0,0,0.3)';
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 py-12 overflow-hidden" style={{ background: '#0A1628' }}>
      {/* Ambient blobs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="blob absolute w-[500px] h-[500px] opacity-20 -top-32 -right-32"
          style={{ background: 'radial-gradient(circle, #4088C6 0%, transparent 70%)' }}
        />
        <div
          className="blob-2 absolute w-[400px] h-[400px] opacity-15 -bottom-24 -left-24"
          style={{ background: 'radial-gradient(circle, #F5A623 0%, transparent 70%)' }}
        />
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'linear-gradient(rgba(64,136,198,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(64,136,198,0.5) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <a href="/home" className="group">
            <div className="transition-transform duration-300 group-hover:scale-105">
              <AppLogo variant="light" size={130} className="cursor-pointer" />
            </div>
          </a>
        </div>

        {/* Glassmorphism Card */}
        <div
          className="rounded-3xl p-8 relative overflow-hidden"
          style={{
            background: 'rgba(15, 30, 53, 0.75)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: '1px solid rgba(64, 136, 198, 0.25)',
            boxShadow: '0 32px 64px rgba(0,0,0,0.5), 0 0 0 1px rgba(64,136,198,0.1), inset 0 1px 0 rgba(255,255,255,0.05)',
          }}
        >
          {/* Top accent line */}
          <div
            className="absolute top-0 left-0 right-0 h-px"
            style={{ background: 'linear-gradient(90deg, transparent, #F5A623, #4088C6, #F5A623, transparent)' }}
          />

          <h1 className="text-2xl font-bold text-white mb-1">Create your account</h1>
          <p className="text-sm mb-5" style={{ color: '#94A3B8' }}>Join thousands of travellers across Africa</p>

          {/* Role selector */}
          <div
            className="rounded-2xl p-1 mb-5"
            style={{ background: 'rgba(10, 22, 40, 0.6)', border: '1px solid rgba(64,136,198,0.2)' }}
          >
            <div className="flex">
              {(['guest', 'host'] as Role[]).map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRole(r)}
                  className="flex-1 py-2.5 text-sm font-semibold rounded-xl transition-all duration-300 flex items-center justify-center gap-2"
                  style={
                    role === r
                      ? {
                          background: 'linear-gradient(135deg, #4088C6, #5BA3D9)',
                          color: '#fff',
                          boxShadow: '0 4px 16px rgba(64,136,198,0.4)',
                        }
                      : { color: '#94A3B8' }
                  }
                >
                  <span>{r === 'guest' ? '🧳' : '🏠'}</span>
                  {r === 'guest' ? 'Guest' : 'Host'}
                </button>
              ))}
            </div>
            {role === 'host' && (
              <div
                className="mx-1 mb-1 mt-2 rounded-xl px-3 py-2.5"
                style={{ background: 'rgba(245,166,35,0.08)', border: '1px solid rgba(245,166,35,0.2)' }}
              >
                <p className="text-xs font-semibold mb-0.5" style={{ color: '#F5A623' }}>Registering as a Host</p>
                <p className="text-xs" style={{ color: '#94A3B8' }}>List properties and manage bookings after completing your profile.</p>
              </div>
            )}
          </div>

          {/* Step indicator */}
          <div className="flex items-center gap-2 mb-6">
            {[1, 2].map((s) => (
              <React.Fragment key={s}>
                <div
                  className="flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold transition-all duration-300"
                  style={
                    s < step
                      ? { background: 'linear-gradient(135deg, #F5A623, #F7BC55)', color: '#0A1628' }
                      : s === step
                      ? { background: 'linear-gradient(135deg, #4088C6, #5BA3D9)', color: '#fff', boxShadow: '0 4px 12px rgba(64,136,198,0.4)' }
                      : { background: 'rgba(64,136,198,0.1)', color: '#475569', border: '1px solid rgba(64,136,198,0.2)' }
                  }
                >
                  {s < step ? (
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : s}
                </div>
                {s < 2 && (
                  <div
                    className="flex-1 h-px transition-all duration-500"
                    style={{ background: s < step ? 'linear-gradient(90deg, #F5A623, #4088C6)' : 'rgba(64,136,198,0.2)' }}
                  />
                )}
              </React.Fragment>
            ))}
            <span className="text-xs ml-1" style={{ color: '#475569' }}>Step {step} of 2</span>
          </div>

          {/* Step 1: Personal Info */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: '#CBD5E1' }}>First name</label>
                  <input
                    type="text"
                    value={form.firstName}
                    onChange={(e) => update('firstName', e.target.value)}
                    placeholder="John"
                    className="w-full rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none transition-all duration-200"
                    style={inputStyle(!!errors.firstName)}
                    onFocus={handleFocus}
                    onBlur={(e) => handleBlur(e, !!errors.firstName)}
                  />
                  {errors.firstName && <p className="text-xs mt-1" style={{ color: '#F87171' }}>{errors.firstName}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: '#CBD5E1' }}>Last name</label>
                  <input
                    type="text"
                    value={form.lastName}
                    onChange={(e) => update('lastName', e.target.value)}
                    placeholder="Doe"
                    className="w-full rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none transition-all duration-200"
                    style={inputStyle(!!errors.lastName)}
                    onFocus={handleFocus}
                    onBlur={(e) => handleBlur(e, !!errors.lastName)}
                  />
                  {errors.lastName && <p className="text-xs mt-1" style={{ color: '#F87171' }}>{errors.lastName}</p>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1.5" style={{ color: '#CBD5E1' }}>Email address</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                  placeholder="you@example.com"
                  className="w-full rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none transition-all duration-200"
                  style={inputStyle(!!errors.email)}
                  onFocus={handleFocus}
                  onBlur={(e) => handleBlur(e, !!errors.email)}
                />
                {errors.email && <p className="text-xs mt-1" style={{ color: '#F87171' }}>{errors.email}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1.5" style={{ color: '#CBD5E1' }}>
                  Phone number{' '}
                  <span style={{ color: '#475569', fontWeight: 400 }}>(optional)</span>
                </label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => update('phone', e.target.value)}
                  placeholder="+256 700 000 000"
                  className="w-full rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none transition-all duration-200"
                  style={inputStyle(!!errors.phone)}
                  onFocus={handleFocus}
                  onBlur={(e) => handleBlur(e, !!errors.phone)}
                />
                {errors.phone && <p className="text-xs mt-1" style={{ color: '#F87171' }}>{errors.phone}</p>}
              </div>

              <button
                type="button"
                onClick={handleNext}
                className="w-full font-bold py-3.5 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 mt-2"
                style={{
                  background: 'linear-gradient(135deg, #4088C6, #5BA3D9)',
                  color: '#fff',
                  boxShadow: '0 8px 24px rgba(64,136,198,0.4)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 12px 32px rgba(64,136,198,0.6)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 8px 24px rgba(64,136,198,0.4)'; e.currentTarget.style.transform = 'none'; }}
              >
                Continue
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}

          {/* Step 2: Password */}
          {step === 2 && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-1.5" style={{ color: '#CBD5E1' }}>Create password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={(e) => update('password', e.target.value)}
                    placeholder="Min. 8 characters"
                    className="w-full rounded-xl px-4 py-3 pr-11 text-sm text-white placeholder-slate-500 focus:outline-none transition-all duration-200"
                    style={inputStyle(!!errors.password)}
                    onFocus={handleFocus}
                    onBlur={(e) => handleBlur(e, !!errors.password)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                    style={{ color: '#475569' }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = '#94A3B8')}
                    onMouseLeave={(e) => (e.currentTarget.style.color = '#475569')}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {showPassword ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      ) : (
                        <>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </>
                      )}
                    </svg>
                  </button>
                </div>
                {form.password && (
                  <div className="mt-2">
                    <div className="flex gap-1 mb-1">
                      {[1, 2, 3, 4].map((i) => (
                        <div
                          key={i}
                          className="flex-1 h-1.5 rounded-full transition-all duration-300"
                          style={{ background: i <= strength ? strengthColors[strength] : 'rgba(64,136,198,0.15)' }}
                        />
                      ))}
                    </div>
                    <p className="text-xs" style={{ color: '#94A3B8' }}>
                      Strength:{' '}
                      <span className="font-semibold" style={{ color: strengthColors[strength] }}>
                        {strengthLabel}
                      </span>
                    </p>
                  </div>
                )}
                {errors.password && <p className="text-xs mt-1" style={{ color: '#F87171' }}>{errors.password}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1.5" style={{ color: '#CBD5E1' }}>Confirm password</label>
                <div className="relative">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    value={form.confirmPassword}
                    onChange={(e) => update('confirmPassword', e.target.value)}
                    placeholder="Re-enter your password"
                    className="w-full rounded-xl px-4 py-3 pr-11 text-sm text-white placeholder-slate-500 focus:outline-none transition-all duration-200"
                    style={inputStyle(!!errors.confirmPassword)}
                    onFocus={handleFocus}
                    onBlur={(e) => handleBlur(e, !!errors.confirmPassword)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                    style={{ color: '#475569' }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = '#94A3B8')}
                    onMouseLeave={(e) => (e.currentTarget.style.color = '#475569')}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {showConfirm ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      ) : (
                        <>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </>
                      )}
                    </svg>
                  </button>
                </div>
                {errors.confirmPassword && <p className="text-xs mt-1" style={{ color: '#F87171' }}>{errors.confirmPassword}</p>}
              </div>

              {/* Terms */}
              <label className="flex items-start gap-3 cursor-pointer group">
                <div className="relative mt-0.5 flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={form.agreeTerms}
                    onChange={(e) => update('agreeTerms', e.target.checked)}
                    className="sr-only"
                  />
                  <div
                    className="w-4 h-4 rounded flex items-center justify-center transition-all duration-200"
                    style={{
                      background: form.agreeTerms ? 'linear-gradient(135deg, #4088C6, #5BA3D9)' : 'rgba(10,22,40,0.7)',
                      border: form.agreeTerms ? 'none' : '1px solid rgba(64,136,198,0.35)',
                    }}
                    onClick={() => update('agreeTerms', !form.agreeTerms)}
                  >
                    {form.agreeTerms && (
                      <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </div>
                <span className="text-sm" style={{ color: '#94A3B8' }}>
                  I agree to the{' '}
                  <a href="#" className="font-semibold transition-colors" style={{ color: '#F5A623' }}>Terms of Service</a>
                  {' '}and{' '}
                  <a href="#" className="font-semibold transition-colors" style={{ color: '#F5A623' }}>Privacy Policy</a>
                </span>
              </label>
              {errors.agreeTerms && <p className="text-xs -mt-2" style={{ color: '#F87171' }}>{errors.agreeTerms}</p>}

              <div className="flex gap-3 mt-2">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex-1 font-semibold py-3.5 rounded-xl transition-all duration-200"
                  style={{
                    background: 'rgba(64,136,198,0.08)',
                    border: '1px solid rgba(64,136,198,0.25)',
                    color: '#94A3B8',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(64,136,198,0.15)'; e.currentTarget.style.color = '#CBD5E1'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(64,136,198,0.08)'; e.currentTarget.style.color = '#94A3B8'; }}
                >
                  ← Back
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-[2] font-bold py-3.5 rounded-xl transition-all duration-300 flex items-center justify-center gap-2"
                  style={{
                    background: loading ? 'rgba(64,136,198,0.5)' : 'linear-gradient(135deg, #4088C6, #5BA3D9)',
                    color: '#fff',
                    boxShadow: loading ? 'none' : '0 8px 24px rgba(64,136,198,0.4)',
                  }}
                  onMouseEnter={(e) => { if (!loading) { e.currentTarget.style.boxShadow = '0 12px 32px rgba(64,136,198,0.6)'; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = loading ? 'none' : '0 8px 24px rgba(64,136,198,0.4)'; e.currentTarget.style.transform = 'none'; }}
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Creating account…
                    </>
                  ) : (
                    `Create ${role === 'guest' ? 'Guest' : 'Host'} Account`
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Sign in link */}
          <p className="text-center text-sm mt-6" style={{ color: '#94A3B8' }}>
            Already have an account?{' '}
            <a
              href="/login"
              className="font-semibold transition-colors"
              style={{ color: '#F5A623' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#F7BC55')}
              onMouseLeave={(e) => (e.currentTarget.style.color = '#F5A623')}
            >
              Sign in
            </a>
          </p>
        </div>

        {/* Back link */}
        <p className="text-center text-sm mt-6" style={{ color: '#475569' }}>
          <a
            href="/home"
            className="transition-colors"
            onMouseEnter={(e) => (e.currentTarget.style.color = '#94A3B8')}
            onMouseLeave={(e) => (e.currentTarget.style.color = '#475569')}
          >
            ← Back to home
          </a>
        </p>
      </div>
    </div>
  );
}
